import { useEffect, useState } from 'react';
import {
  ToastContainer,
  ThemeProvider,
  FooterSimplified,
  UserInfoProvider,
  TenantTypeProvider,
  GoogleOneTap,
  DeviceProvider
} from '@spotahome/ui-library';
import {
  CustomDimensionsManager,
  constants
} from '@spotahome/ecommerce-tracking';
import { useSoyuzData, useSoyuzLocales } from '@spotahome/soyuz/client';
import getGqlClient from '@spotahome/marketplace-common/src/graphql/client';
import { isLogged } from '@spotahome/marketplace-common/src/graphql/queries/user';
import { getFavorites } from '@spotahome/marketplace-common/src/graphql/queries/favorites';
import OptInModalWrapper from '@spotahome/marketplace-common/src/components/OptInModalWrapper';
import HeaderContainer from '@spotahome/marketplace-common/src/components/HeaderContainer';

import LoginModal from '@spotahome/marketplace-common/src/components/login/LoginModal';

import '@spotahome/marketplace-common/src/scss/init.scss';

import Favorites from './components/Favorites';

const FAVORITES_UPDATE_INTERVAL_MS = 60000;

async function getFavoritesRequest(currentLocale, graphqlClient) {
  try {
    const response = await getFavorites(graphqlClient)(currentLocale);

    return response.data.favorites.filter(fav => fav);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return [];
  }
}

const FavoritesPage = () => {
  const { [FavoritesPage.NAME]: requestedData } = useSoyuzData();
  const { current: currentLocale } = useSoyuzLocales();
  const {
    isLogged: isUserLogged,
    favorites: homecards,
    userId,
    deviceType
  } = requestedData;

  const [favorites, setFavorites] = useState(homecards);

  const handleFavorite = (id, isFavorite) => {
    if (!isFavorite) {
      setFavorites(prevFavorites =>
        prevFavorites.filter(favorite => favorite.id !== id)
      );
    }
  };

  useEffect(() => {
    const request = async () => {
      try {
        const newFavorites = await getFavoritesRequest(
          currentLocale,
          getGqlClient()
        );

        setFavorites(newFavorites);
      } catch (error) {
        console.error(error);
      }
    };

    const timer = setInterval(request, FAVORITES_UPDATE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  return (
    <ThemeProvider
      flags={{
        newButtons: true
      }}
    >
      <UserInfoProvider>
        <TenantTypeProvider>
          <DeviceProvider deviceType={deviceType}>
            <ToastContainer />
            {!isUserLogged && <GoogleOneTap />}
            <HeaderContainer />
            <Favorites
              isLogged={isUserLogged}
              homecards={favorites}
              onToggleFavorite={handleFavorite}
              userId={userId}
            />
            <LoginModal />
            <OptInModalWrapper />
            <FooterSimplified />
          </DeviceProvider>
        </TenantTypeProvider>
      </UserInfoProvider>
    </ThemeProvider>
  );
};

FavoritesPage.NAME = 'FavoritesPage';

FavoritesPage.requestAllData = async (req, _, { graphqlClient }) => {
  try {
    const responseIsLogged = await isLogged(graphqlClient)();

    const { locale, userId } = req;

    const favorites = await getFavoritesRequest(locale, graphqlClient);

    const cdManager = CustomDimensionsManager();
    cdManager.setSearchCommonCustomDimensions({
      userId,
      locale,
      pageType: constants.TRACKING_PAGE_TYPES.FAVORITES
    });

    return {
      headers: {
        scripts: [{ innerHTML: cdManager.flush() }],
        links: []
      },
      data: {
        isLogged:
          responseIsLogged && responseIsLogged.data
            ? responseIsLogged.data.isLogged
            : false,
        favorites,
        userId,
        deviceType: req.device.type
      }
    };
  } catch (error) {
    console.error(error);
  }

  return {
    headers: {},
    body: {}
  };
};

export default FavoritesPage;
